import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  SquaresFour, Images, ChatCircle, Tag, Ticket, TrayArrowDown, EnvelopeSimple,
  SignOut, Plus, PencilSimple, Trash, ToggleLeft, ToggleRight, X, ArrowSquareOut, DownloadSimple, CurrencyDollar, ArrowsClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const TABS = [
  { key: "overview", label: "Overview", icon: SquaresFour },
  { key: "portfolio", label: "Portfolio", icon: Images },
  { key: "testimonials", label: "Testimonials", icon: ChatCircle },
  { key: "pricing", label: "Pricing", icon: Tag },
  { key: "codes", label: "Referral Codes", icon: Ticket },
  { key: "submissions", label: "Referrals", icon: TrayArrowDown },
  { key: "payments", label: "Payments", icon: CurrencyDollar },
  { key: "subscriptions", label: "Subscriptions", icon: ArrowsClockwise },
  { key: "contacts", label: "Inquiries", icon: EnvelopeSimple },
];

// field schemas
const SCHEMAS = {
  portfolio: {
    endpoint: "/admin/portfolio",
    idKey: "slug",
    fields: [
      { name: "business_name", label: "Business name", type: "text" },
      { name: "industry", label: "Industry", type: "text" },
      { name: "slug", label: "Slug (url)", type: "text" },
      { name: "description", label: "Description", type: "textarea" },
      { name: "image", label: "Image URL", type: "text" },
      { name: "technologies", label: "Technologies", type: "list" },
      { name: "results", label: "Results", type: "list" },
      { name: "goals", label: "Goals", type: "textarea" },
      { name: "process", label: "Process", type: "textarea" },
      { name: "before", label: "Before", type: "textarea" },
      { name: "after", label: "After", type: "textarea" },
      { name: "order", label: "Order", type: "number" },
      { name: "featured", label: "Featured", type: "bool" },
    ],
  },
  testimonials: {
    endpoint: "/admin/testimonials",
    idKey: "testimonial_id",
    fields: [
      { name: "name", label: "Client name", type: "text" },
      { name: "business", label: "Business", type: "text" },
      { name: "rating", label: "Rating (1-5)", type: "number", default: 5 },
      { name: "review", label: "Review", type: "textarea" },
      { name: "photo", label: "Photo URL", type: "text" },
      { name: "order", label: "Order", type: "number" },
    ],
  },
  pricing: {
    endpoint: "/admin/pricing",
    idKey: "plan_id",
    fields: [
      { name: "name", label: "Plan name", type: "text" },
      { name: "price", label: "One-time price", type: "number" },
      { name: "period", label: "Period", type: "text" },
      { name: "tagline", label: "Tagline", type: "text" },
      { name: "features", label: "Features", type: "list" },
      { name: "cta", label: "CTA label", type: "text" },
      { name: "order", label: "Order", type: "number" },
      { name: "monthly_price", label: "Monthly price ($/mo)", type: "number" },
      { name: "stripe_price_id", label: "Stripe recurring Price ID (price_...)", type: "text" },
      { name: "highlighted", label: "Highlighted", type: "bool" },
    ],
  },
  codes: {
    endpoint: "/admin/referral-codes",
    idKey: "code",
    fields: [
      { name: "code", label: "Code", type: "text" },
      { name: "discount_percent", label: "Discount %", type: "number", default: 10 },
      { name: "referrer_business", label: "Referrer business", type: "text" },
      { name: "referrer_email", label: "Referrer email", type: "text" },
      { name: "max_uses", label: "Max uses", type: "number", default: 100 },
      { name: "active", label: "Active", type: "bool", default: true },
    ],
  },
};

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (!loading && !user && !window.location.hash?.includes("session_id=")) {
      navigate("/admin/login", { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]"><div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (!user) return null;

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="w-60 border-r border-black/10 bg-white flex flex-col fixed h-screen">
        <div className="p-6 border-b border-black/10">
          <div className="font-display text-xl font-black tracking-tighter">KWD Admin</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mt-1">Content CMS</div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                data-testid={`admin-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                  tab === t.key ? "bg-black text-white" : "text-zinc-600 hover:bg-zinc-100"
                }`}
              >
                <Icon size={18} weight={tab === t.key ? "fill" : "regular"} /> {t.label}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-black/10">
          <div className="flex items-center gap-3 px-3 py-2">
            {user.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />}
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{user.name}</div>
              <div className="text-xs text-zinc-500 truncate">{user.email}</div>
            </div>
          </div>
          <button data-testid="admin-logout" onClick={doLogout} className="mt-1 w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors">
            <SignOut size={18} /> Sign out
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 ml-60 p-8">
        {tab === "overview" && <Overview />}
        {tab === "portfolio" && <EntityManager type="portfolio" title="Portfolio Projects" labelKey="business_name" />}
        {tab === "testimonials" && <EntityManager type="testimonials" title="Testimonials" labelKey="name" />}
        {tab === "pricing" && <EntityManager type="pricing" title="Pricing Plans" labelKey="name" />}
        {tab === "codes" && <CodesManager />}
        {tab === "submissions" && <ReadOnlyTable endpoint="/admin/referral-submissions" title="Referral Submissions" columns={[["created_at","Date"],["name","Name"],["business_name","Business"],["code","Code"],["discount_percent","Discount %"],["referrer_business","Referrer"],["proof_filename","Proof"]]} />}
        {tab === "payments" && <ReadOnlyTable endpoint="/admin/payments" title="Payments" columns={[["created_at","Date"],["method","Method"],["plan","Plan"],["total","Total ($)"],["discount","Discount ($)"],["referral_code","Code"],["payer_email","Email"],["status","Status"]]} />}
        {tab === "subscriptions" && <ReadOnlyTable endpoint="/admin/subscriptions" title="Subscriptions" columns={[["created_at","Started"],["customer_name","Name"],["customer_email","Email"],["plan","Plan"],["status","Status"],["current_period_end","Renews"],["stripe_subscription_id","Sub ID"]]} />}
        {tab === "contacts" && <ReadOnlyTable endpoint="/admin/contacts" title="Contact Inquiries" columns={[["created_at","Date"],["name","Name"],["business_name","Business"],["email","Email"],["services","Services"]]} />}
      </main>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  const cards = [
    ["Portfolio", "portfolio"], ["Testimonials", "testimonials"], ["Pricing Plans", "pricing"],
    ["Referral Codes", "referral_codes"], ["Referrals", "referral_submissions"], ["Payments", "payments"], ["Subscriptions", "subscriptions"], ["Inquiries", "contacts"], ["Subscribers", "newsletter"],
  ];
  return (
    <div>
      <h1 className="font-display text-3xl font-black tracking-tighter">Overview</h1>
      <p className="text-zinc-500 mt-1">Your content and lead activity at a glance.</p>
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map(([label, key]) => (
          <div key={key} className="bg-white border border-black/10 p-6">
            <div className="text-sm text-zinc-500">{label}</div>
            <div className="mt-2 font-display text-4xl font-black tracking-tighter">{stats ? stats[key] ?? 0 : "—"}</div>
          </div>
        ))}
      </div>
      <a href="/" target="_blank" rel="noreferrer" className="mt-8 inline-flex items-center gap-2 text-sm font-medium underline underline-offset-4">
        View live site <ArrowSquareOut size={16} />
      </a>
    </div>
  );
}

function emptyFrom(fields) {
  const o = {};
  fields.forEach((f) => {
    if (f.default !== undefined) { o[f.name] = f.default; return; }
    o[f.name] = f.type === "list" ? [] : f.type === "bool" ? false : f.type === "number" ? 0 : "";
  });
  return o;
}

function EntityManager({ type, title, labelKey }) {
  const schema = SCHEMAS[type];
  const publicEndpoint = type === "codes" ? "/admin/referral-codes" : `/${type}`;
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get(publicEndpoint).then((r) => setItems(r.data)).catch(() => {});
  }, [publicEndpoint]);
  useEffect(() => { load(); }, [load]);

  const remove = async (item) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`${schema.endpoint}/${item[schema.idKey]}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tighter">{title}</h1>
          <p className="text-zinc-500 mt-1">{items.length} item{items.length !== 1 ? "s" : ""}</p>
        </div>
        <button data-testid={`admin-add-${type}`} onClick={() => setEditing(emptyFrom(schema.fields))} className="inline-flex items-center gap-2 rounded-full bg-black text-white font-medium px-5 py-2.5 hover:bg-zinc-800 transition-colors">
          <Plus size={18} /> Add
        </button>
      </div>

      <div className="mt-6 bg-white border border-black/10 divide-y divide-black/10">
        {items.map((item) => (
          <div key={item[schema.idKey]} data-testid={`admin-row-${type}`} className="flex items-center justify-between p-4">
            <div className="min-w-0 flex items-center gap-3">
              {item.image && <img src={item.image} alt="" className="w-14 h-10 object-cover border border-black/10" />}
              {item.photo && <img src={item.photo} alt="" className="w-10 h-10 rounded-full object-cover" />}
              <div className="min-w-0">
                <div className="font-medium truncate">{item[labelKey]}</div>
                <div className="text-sm text-zinc-500 truncate">{item.industry || item.business || item.tagline || ""}</div>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button data-testid={`admin-edit-${type}`} onClick={() => setEditing(item)} className="p-2 hover:bg-zinc-100 rounded-lg transition-colors"><PencilSimple size={18} /></button>
              <button data-testid={`admin-delete-${type}`} onClick={() => remove(item)} className="p-2 hover:bg-zinc-100 rounded-lg transition-colors text-red-600"><Trash size={18} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="p-8 text-center text-zinc-500">No items yet.</div>}
      </div>

      {editing && (
        <EntityForm
          schema={schema}
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function EntityForm({ schema, initial, onClose, onSaved }) {
  const [data, setData] = useState(initial);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial[schema.idKey] && initial[schema.idKey] !== "";

  const set = (name, val) => setData((d) => ({ ...d, [name]: val }));

  const save = async () => {
    setSaving(true);
    const payload = {};
    schema.fields.forEach((f) => { payload[f.name] = data[f.name]; });
    try {
      if (isEdit) {
        await api.put(`${schema.endpoint}/${initial[schema.idKey]}`, payload);
      } else {
        await api.post(schema.endpoint, payload);
      }
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const inputCls = "w-full bg-white border border-black/15 px-3 py-2 outline-none focus:border-black transition-colors text-sm";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl max-h-[88vh] overflow-y-auto border border-black/10" onClick={(e) => e.stopPropagation()} data-testid="admin-entity-form">
        <div className="sticky top-0 bg-white border-b border-black/10 px-6 py-4 flex items-center justify-between">
          <h2 className="font-display text-xl font-bold tracking-tight">{isEdit ? "Edit" : "Add"} item</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-100 rounded-lg"><X size={20} /></button>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {schema.fields.map((f) => (
            <div key={f.name} className={f.type === "textarea" || f.type === "list" ? "sm:col-span-2" : ""}>
              <label className="text-sm font-medium">{f.label}</label>
              {f.type === "text" && <input className={`mt-1.5 ${inputCls}`} value={data[f.name] ?? ""} onChange={(e) => set(f.name, e.target.value)} />}
              {f.type === "number" && <input type="number" className={`mt-1.5 ${inputCls}`} value={data[f.name] ?? 0} onChange={(e) => set(f.name, parseInt(e.target.value || "0", 10))} />}
              {f.type === "textarea" && <textarea rows={3} className={`mt-1.5 ${inputCls} resize-none`} value={data[f.name] ?? ""} onChange={(e) => set(f.name, e.target.value)} />}
              {f.type === "list" && <textarea rows={3} className={`mt-1.5 ${inputCls} resize-none`} placeholder="One per line" value={(data[f.name] || []).join("\n")} onChange={(e) => set(f.name, e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} />}
              {f.type === "bool" && (
                <button type="button" onClick={() => set(f.name, !data[f.name])} className="mt-1.5 flex items-center gap-2 text-sm">
                  {data[f.name] ? <ToggleRight size={28} weight="fill" /> : <ToggleLeft size={28} />} {data[f.name] ? "Yes" : "No"}
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="sticky bottom-0 bg-white border-t border-black/10 px-6 py-4 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-full border border-black/15 font-medium px-5 py-2.5 hover:bg-zinc-100 transition-colors">Cancel</button>
          <button data-testid="admin-save-entity" onClick={save} disabled={saving} className="rounded-full bg-black text-white font-medium px-6 py-2.5 hover:bg-zinc-800 transition-colors disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

function CodesManager() {
  const [codes, setCodes] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => { api.get("/admin/referral-codes").then((r) => setCodes(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (code) => {
    try { await api.patch(`/admin/referral-codes/${code}/toggle`); load(); } catch { toast.error("Failed"); }
  };
  const remove = async (code) => {
    if (!window.confirm("Delete code?")) return;
    try { await api.delete(`/admin/referral-codes/${code}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tighter">Referral Codes</h1>
          <p className="text-zinc-500 mt-1">Generate, activate, and track referral codes.</p>
        </div>
        <button data-testid="admin-add-codes" onClick={() => setEditing(emptyFrom(SCHEMAS.codes.fields))} className="inline-flex items-center gap-2 rounded-full bg-black text-white font-medium px-5 py-2.5 hover:bg-zinc-800 transition-colors">
          <Plus size={18} /> New Code
        </button>
      </div>

      <div className="mt-6 bg-white border border-black/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-black/10 text-left text-zinc-500">
            <tr>
              <th className="p-4 font-medium">Code</th><th className="p-4 font-medium">Discount</th>
              <th className="p-4 font-medium">Referrer</th><th className="p-4 font-medium">Uses</th>
              <th className="p-4 font-medium">Status</th><th className="p-4 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/10">
            {codes.map((c) => (
              <tr key={c.code} data-testid={`admin-code-row-${c.code}`}>
                <td className="p-4 font-mono font-medium">{c.code}</td>
                <td className="p-4">{c.discount_percent}%</td>
                <td className="p-4">{c.referrer_business}</td>
                <td className="p-4">{c.uses}/{c.max_uses}</td>
                <td className="p-4">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${c.active ? "bg-black text-white" : "bg-zinc-200 text-zinc-600"}`}>{c.active ? "Active" : "Inactive"}</span>
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end gap-1">
                    <button data-testid={`admin-toggle-code-${c.code}`} onClick={() => toggle(c.code)} className="p-2 hover:bg-zinc-100 rounded-lg" title="Toggle active">{c.active ? <ToggleRight size={20} weight="fill" /> : <ToggleLeft size={20} />}</button>
                    <button onClick={() => setEditing(c)} className="p-2 hover:bg-zinc-100 rounded-lg"><PencilSimple size={18} /></button>
                    <button onClick={() => remove(c.code)} className="p-2 hover:bg-zinc-100 rounded-lg text-red-600"><Trash size={18} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {codes.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-zinc-500">No codes yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && <EntityForm schema={SCHEMAS.codes} initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ReadOnlyTable({ endpoint, title, columns }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(endpoint).then((r) => setRows(r.data)).catch(() => {}); }, [endpoint]);
  const fmt = (v, key) => {
    if (v == null || v === "") return "—";
    if (key === "created_at") return new Date(v).toLocaleDateString();
    return String(v);
  };

  const exportCsv = () => {
    if (!rows.length) { toast.error("No records to export"); return; }
    const esc = (val) => {
      const s = val == null ? "" : String(val);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = columns.map(([, label]) => esc(label)).join(",");
    const body = rows.map((row) => columns.map(([key]) => esc(row[key])).join(",")).join("\n");
    const csv = header + "\n" + body;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.toLowerCase().replace(/[^a-z]+/g, "-")}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} record${rows.length !== 1 ? "s" : ""}`);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tighter">{title}</h1>
          <p className="text-zinc-500 mt-1">{rows.length} record{rows.length !== 1 ? "s" : ""}</p>
        </div>
        <button
          data-testid="admin-export-csv"
          onClick={exportCsv}
          className="inline-flex items-center gap-2 rounded-full bg-black text-white font-medium px-5 py-2.5 hover:bg-zinc-800 transition-colors"
        >
          <DownloadSimple size={18} /> Export CSV
        </button>
      </div>
      <div className="mt-6 bg-white border border-black/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-black/10 text-left text-zinc-500">
            <tr>{columns.map(([, label]) => <th key={label} className="p-4 font-medium">{label}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-black/10">
            {rows.map((row, i) => (
              <tr key={i} data-testid="admin-readonly-row">
                {columns.map(([key]) => <td key={key} className="p-4">{fmt(row[key], key)}</td>)}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={columns.length} className="p-8 text-center text-zinc-500">No records yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
